<script setup lang="ts">
import type * as openapi from '@/helpers/api/types/openapiOverrides';
import type { BookingStop } from '@/models/advancedJobBooking';

import { computed, ref } from 'vue';

import KSwitchField from '@/components/fields/KSwitchField.vue';
import KTextField from '@/components/fields/KTextField.vue';
import SaveToAddressBookField from '@/components/fields/SaveToAddressBookField.vue';
import SuburbAutocompleteField from '@/components/fields/SuburbAutocompleteField.vue';
import { requiredValidationRule } from '@/helpers/validationRules';
import { createEmptyStop } from '@/models/advancedJobBooking';

export interface Props {
  customerCode?: string;
  addressItems?: openapi.AddressGETOpenapi[];
  addressLoading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  customerCode: '',
  addressItems: () => [],
  addressLoading: false,
});

const model = defineModel<BookingStop>({
  default: () => createEmptyStop(1),
});

const comboboxOpen = ref(false);

const isWarehouseStop = computed(() => model.value.warehouse);
const fieldsDisabled = computed(() => !props.customerCode);
const companyRules = computed(() =>
  fieldsDisabled.value ? [] : [requiredValidationRule],
);
const addressRules = computed(() =>
  fieldsDisabled.value || isWarehouseStop.value ? [] : [requiredValidationRule],
);

const warehouseToggle = computed({
  get: () => model.value.warehouse,
  set: (value: boolean) => {
    model.value = {
      ...model.value,
      warehouse: value,
      name: '',
      note_name: '',
      note_phone: '',
      address_line1: '',
      address_line2: '',
      suburb: '',
      suburb_id: null,
      postcode: '',
      state: '',
    };
  },
});

const saveToAddressBook = computed({
  get: () => model.value.save_to_address_book,
  set: (value: boolean) => {
    model.value = { ...model.value, save_to_address_book: value };
  },
});

const savedAddress = computed<openapi.AddressGETOpenapi | null>(
  () => props.addressItems.find((a) => a.is_default) ?? null,
);

const addressBookItems = computed(() => {
  const filtered = isWarehouseStop.value
    ? props.addressItems.filter((a) => a.warehouse)
    : props.addressItems.filter((a) => !a.warehouse);
  const seen = new Set<string>();
  return filtered.filter((a) => {
    const key = a.name;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
});

let lastSuburbAdapter: openapi.SuburbOpenapi | null = null;
const suburbAdapter = computed<openapi.SuburbOpenapi | null>({
  get() {
    if (!model.value.suburb) {
      lastSuburbAdapter = null;
      return null;
    }
    const next: openapi.SuburbOpenapi = {
      id: model.value.suburb_id ?? 0,
      name: model.value.suburb,
      state: model.value.state || '',
      postcode: model.value.postcode || '',
      zone: '',
      status: 0,
    };
    if (
      lastSuburbAdapter?.id === next.id &&
      lastSuburbAdapter.name === next.name &&
      lastSuburbAdapter.state === next.state &&
      lastSuburbAdapter.postcode === next.postcode
    ) {
      return lastSuburbAdapter;
    }
    lastSuburbAdapter = next;
    return next;
  },
  set(value: openapi.SuburbOpenapi | null) {
    if (value?.name) {
      model.value = {
        ...model.value,
        suburb: value.name,
        suburb_id: value.id,
        postcode: value.postcode ?? model.value.postcode,
        state: value.state ?? model.value.state,
        address_line1: model.value.address_line1 || '',
      };
    } else {
      model.value = { ...model.value, suburb: '', suburb_id: null };
    }
  },
});

function applyAddressBookEntry(entry: openapi.AddressGETOpenapi): void {
  model.value = {
    ...model.value,
    name: entry.name,
    address_line1: entry.line1 ?? '',
    address_line2: entry.line2 ?? '',
    suburb: entry.suburb?.name ?? '',
    suburb_id: entry.suburb?.id ?? null,
    postcode: entry.suburb?.postcode ?? '',
    state: entry.suburb?.state ?? '',
    note_name: entry.note_name,
    note_phone: entry.note_phone,
    note_text: entry.note_text,
  };
}

function handleNameChange(val: string | null): void {
  if (!val) return;
  const entry = addressBookItems.value.find((a) => a.name === val);
  if (entry) applyAddressBookEntry(entry);
}

function handleClear(): void {
  model.value = {
    ...model.value,
    address_line1: '',
    address_line2: '',
    suburb: '',
    suburb_id: null,
    postcode: '',
    state: '',
  };
}

function selectSavedAddress(): void {
  if (savedAddress.value) applyAddressBookEntry(savedAddress.value);
  comboboxOpen.value = false;
}
</script>

<template>
  <fieldset
    :class="$style.stopCustomerWarehouseFieldset"
    data-testid="stopCustomerWarehouseFieldset"
  >
    <div :class="$style.addressHeader">
      <span :class="$style.addressLabel">Address</span>
      <div :class="$style.warehouseToggle">
        <span :class="$style.warehouseLabel">KK WAREHOUSE</span>
        <KSwitchField v-model="warehouseToggle" inset is-small hide-details />
      </div>
    </div>
    <v-combobox
      v-model="model.name"
      v-model:menu="comboboxOpen"
      label="Company Name"
      :items="addressBookItems"
      item-title="name"
      item-value="name"
      :rules="companyRules"
      :loading="props.addressLoading"
      :disabled="fieldsDisabled"
      variant="outlined"
      density="compact"
      clearable
      hide-no-data
      :return-object="false"
      class="g-v-text-field"
      @update:model-value="handleNameChange"
      @update:search="
        (val) => {
          if (val) model.name = val;
        }
      "
      @click:clear="handleClear"
    >
      <template #prepend-item>
        <template v-if="!isWarehouseStop && savedAddress">
          <v-list-item :title="savedAddress.name" @click="selectSavedAddress">
            <template #append>
              <v-icon size="16">mdi-home-outline</v-icon>
            </template>
          </v-list-item>
          <v-divider />
        </template>
      </template>
    </v-combobox>
    <div :class="$style.streetRow">
      <KTextField
        v-model="model.address_line2"
        label="Street"
        size="md"
        :disabled="fieldsDisabled || isWarehouseStop"
        :rules="addressRules"
        validate-on="input"
      />
      <KTextField
        v-model="model.address_line1"
        label="Room, level, unit (Optional)"
        size="md"
        :disabled="fieldsDisabled || isWarehouseStop"
      />
    </div>
    <SuburbAutocompleteField
      v-model="suburbAdapter"
      label="Suburb"
      :rules="addressRules"
      :disabled="fieldsDisabled || isWarehouseStop"
      validate-on="input"
    />
    <SaveToAddressBookField
      v-model="saveToAddressBook"
      :disabled="fieldsDisabled"
    />
  </fieldset>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';

.stopCustomerWarehouseFieldset {
  border: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
  .addressHeader {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .addressLabel {
    font-size: 0.875rem;
    font-weight: 500;
    color: colors.$color-text-primary;
  }
  .warehouseToggle {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  .warehouseLabel {
    font-size: 0.75rem;
    font-weight: 500;
    color: colors.$color-text-secondary;
    letter-spacing: 0.04em;
  }
  .streetRow {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
  }
</style>
